import { Component, effect, inject, signal } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LucideChevronRight, LucideX } from '@lucide/angular';
import { TranslateModule } from '@ngx-translate/core';
import { PermissionNode } from '../../models/user.model';
import { LayoutService } from '../../services/layout.service';
import { AuthStore } from '../../stores/auth.store';
import { DynamicIconComponent } from '../../../shared/components/dynamic-icon/dynamic-icon.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterModule,
    LucideChevronRight,
    LucideX,
    TranslateModule,
    DynamicIconComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  layoutService = inject(LayoutService);
  authStore = inject(AuthStore);
  router = inject(Router);

  /** Menu tree được lấy từ AuthStore */
  menuTree = this.authStore.menuTree;

  /** Lưu trữ ID của những Menu đang được mở rộng */
  expandedItems = signal<Set<string>>(new Set());

  constructor() {
    // Dùng effect để tự động refresh khi menuTree thay đổi (quan trọng khi load async)
    effect(() => {
      if (this.menuTree().length > 0) {
        this.refreshExpandedState();
      }
    });
  }

  /** Tự động mở rộng menu cha chứa route đang active khi load trang */
  private refreshExpandedState() {
    const currentUrl = this.router.url.split('?')[0];
    const itemsToExpand = new Set<string>();

    const isMatch = (path: string | null | undefined) => {
      if (!path) return false;
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const normalizedCurrent = currentUrl.startsWith('/')
        ? currentUrl
        : `/${currentUrl}`;
      return (
        normalizedCurrent === normalizedPath ||
        normalizedCurrent.startsWith(normalizedPath + '/')
      );
    };

    this.menuTree().forEach((item) => {
      // Nếu chính nó active hoặc chính nó chứa URL con
      if (isMatch(item.path)) {
        itemsToExpand.add(item.id);
      }
      // Nếu có con đang active
      const hasActiveChild = item.children?.some((child) =>
        isMatch(child.path),
      );
      if (hasActiveChild) {
        itemsToExpand.add(item.id);
      }
    });

    if (itemsToExpand.size > 0) {
      this.expandedItems.update((old) => new Set([...old, ...itemsToExpand]));
    }
  }

  toggleSubMenu(item: PermissionNode) {
    // Nếu click vào một mục có con, tiến hành toggle
    if (item.children && item.children.length > 0) {
      const currentExpanded = new Set(this.expandedItems());
      if (currentExpanded.has(item.id)) {
        currentExpanded.delete(item.id);
      } else {
        currentExpanded.add(item.id);
      }
      this.expandedItems.set(currentExpanded);
    }
    // Nếu là mục lá và đang ở mobile, đóng sidebar
    else if (this.layoutService.isMobile()) {
      this.layoutService.toggleSidebar();
    }
  }

  /** Kiểm tra xem item có đang mở rộng không */
  isExpanded(id: string): boolean {
    return this.expandedItems().has(id);
  }

  /** Kiểm tra xem Menu cha có đang chứa con đang Active hay không */
  isActive(item: PermissionNode): boolean {
    const currentUrl = this.router.url.split('?')[0];

    const isMatch = (path: string | null | undefined) => {
      if (!path) return false;
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const normalizedCurrent = currentUrl.startsWith('/')
        ? currentUrl
        : `/${currentUrl}`;
      return (
        normalizedCurrent === normalizedPath ||
        normalizedCurrent.startsWith(normalizedPath + '/')
      );
    };

    // Nếu chính nó có path khớp
    if (isMatch(item.path)) return true;

    // Nếu có con, kiểm tra xem có con nào khớp link không
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => isMatch(child.path));
    }

    return false;
  }
}
